import React, { useEffect, useMemo, useState, useRef } from 'react';

import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { ContentServiceJobStatus as JobStatus, FkTable } from '../../stores/types/content';
import LoadingSpinner from '../general-components/loading-spinner';
import Colors from '../../utils/colors';
import classNames from 'classnames';
import { showAlert } from '../general-components/alert-service';

interface StylePackLoadingSpinnerProps {
	id: number;
	type: FkTable;
	isForShotEditorModal: boolean;
	targetElement?: HTMLDivElement | null;
}

export default function StylePackLoadingSpinner(props: StylePackLoadingSpinnerProps) {
	const {
		id, type, isForShotEditorModal, targetElement = null,
	} = props;

	const generatedCon = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const generatedConEdits = useSelector((s: GS) => s.GeneratedContentReducer.generatedContentEdits);
	const initializingJobs = useSelector((s: GS) => s.GeneratedContentReducer.initializingJobs);

	const [targetPosition, setTargetPosition] = useState<React.CSSProperties>({});
	const [shouldRender, setShouldRender] = useState<boolean>(false);
	const observerRef = React.useRef<MutationObserver>();

	const item = useMemo(() => {
		if (isForShotEditorModal) {
			return generatedConEdits?.find(
				(j) => j.fkTable === type && j.fkId === id,
			);
		} else {
			return generatedCon?.find(
				(j) => j.fkTable === type && j.fkId === id,
			);
		}
	}, [generatedCon, generatedConEdits, isForShotEditorModal, type, id])
	const initializing = initializingJobs?.get(`${type}-${id}${isForShotEditorModal ? '-draft' : ''}`);
	// Check if the content is a countdown stream
	const isCountdown = useMemo(() => {
		if (!item) return false;
		const hasSeconds = item?.data.find(i => i.key === 'seconds');
		return !!hasSeconds;
	}, [item]);
	
	const initOrProcessing = useMemo(
		() => (!isCountdown && item?.jobStatus !== JobStatus.completed && (initializing || item?.jobStatus === JobStatus.processing || item?.jobStatus === JobStatus.exporting)),
		[initializing, item?.jobStatus, isCountdown]
	);

	function handleMutation(records: MutationRecord[]) {
		records.forEach((record: MutationRecord) => {
			if (record.target instanceof HTMLDivElement) {
				const {
					left, top, width, height, transform,
				} = record.target.style;
				setTargetPosition({
					left, top, width, height, transform,
				});
			}
		});
	}

	useEffect(() => {
		// use target element for the spinner's position, size and rotation and update if it changes
		if (targetElement && initOrProcessing) {
			observerRef.current = new MutationObserver(handleMutation);
			observerRef.current.observe(targetElement, {
				attributes: true,
				attributeFilter: ['style'],
			});
		} else {
			observerRef.current?.disconnect();
		}

		return () => {
			observerRef.current?.disconnect();
		};
	}, [targetElement, initOrProcessing]);

	useEffect(() => {
		if (!shouldRender && initOrProcessing) {
			setShouldRender(true);
		}
	}, [initOrProcessing, shouldRender]);

	useEffect(() => {
		if (item?.jobStatus === JobStatus.failed) {
			showAlert({
				type: 'general-error',
				message: 'Something went wrong, please try again',
				description: '',
				duration: 5000,
				autoWidth: true,
			});
		}
	}, [item?.jobStatus])

	if (!shouldRender) {
		return null;
	}

	return (<div
		className={classNames('style-pack-loading-spinner fade-in', {
			'fade-out': shouldRender && !initOrProcessing
		})}
		style={{
			...targetPosition
		}}
		onAnimationEnd={(e) => {
			if (e.animationName === 'fadeOutAnim') {
				setShouldRender(false);
				setTargetPosition({});
			}
		}}
	>
		<LoadingSpinner
			percHeight={type === FkTable.graphics ? 50 : 12}
			percWidth={type === FkTable.graphics ? 50 : 12}
			height={type === FkTable.graphics ? 42 : 1000}
			width={type === FkTable.graphics ? 42 : 1000}
			color={Colors.paleGreen}
			stroke={4}
			style={{ minHeight: '42px', minWidth: '42px' }}
		/>
	</div>);
}
